import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { bgGradient } from 'src/theme/css';
import { signupUrl } from 'src/config-global';

import Iconify from 'src/components/iconify';
import { varFade, MotionViewport } from 'src/components/animate';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export default function HomeAdvertisement() {
  const theme = useTheme();

  const { user } = useAuthContext();

  const renderDescription = (
    <Box
      sx={{
        flex: 1,
        textAlign: { xs: 'center', md: 'left' },
      }}
    >
      <Box
        component={m.div}
        variants={varFade().inDown}
        sx={{ color: 'common.white', typography: 'h2' }}
      >
        Get started with
        <br /> TechStack today
      </Box>

      <Typography
        component={m.p}
        variants={varFade().inDown}
        sx={{ mt: 2, mb: 4, color: alpha(theme.palette.common.white, 0.8) }}
      >
        Give your team one clear place to grow their skills.
      </Typography>

      <m.div variants={varFade().inUp}>
        <Button
          component={RouterLink}
          href={user ? paths.dashboard.root : signupUrl}
          color="inherit"
          size="large"
          variant="contained"
          startIcon={<Iconify icon="eva:flash-fill" width={24} />}
          sx={{
            color: 'info.dark',
            bgcolor: 'common.white',
            '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9) },
          }}
        >
          Signup
        </Button>
      </m.div>
    </Box>
  );

  const renderImg = (
    <Stack
      component={m.div}
      variants={varFade().inUp}
      alignItems="center"
      justifyContent="center"
      sx={{ flex: 1 }}
    >
      <Box
        component={m.img}
        animate={{
          y: [-16, 0, -16],
        }}
        transition={{ duration: 4, repeat: Infinity }}
        alt="rocket"
        src="/assets/images/home/rocket.webp"
        sx={{ width: 1, maxWidth: 380 }}
      />
    </Stack>
  );

  return (
    <Container component={MotionViewport} sx={{ py: { xs: 10, md: 15 } }}>
      <Stack
        alignItems="center"
        justifyContent="center"
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 5 }}
        sx={{
          ...bgGradient({
            direction: '135deg',
            startColor: theme.palette.info.main,
            endColor: theme.palette.info.dark,
          }),
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          px: { xs: 3, md: 8 },
          py: { xs: 6, md: 10 },
          '&::before': {
            content: '""',
            top: -120,
            right: -120,
            width: 360,
            height: 360,
            position: 'absolute',
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.common.white, 0.08),
          },
        }}
      >
        {renderImg}

        {renderDescription}
      </Stack>
    </Container>
  );
}
